import {Events} from "../minicart/CartEvents";
import {addTouch} from "../touch/Touch";
import Overlay from "../Overlay";
import {Helpers} from "../../../common/js/Helpers";
import SizeCalculator from "../EventgallerySizeCalculator";

    /*
     *    Constructor. Starts up the whole thing :-)
     *
     *    This script is free to use. It has been created by http://www.aplusmedia.de and
     *    can be downloaded on http://www.esteak.net.
     *    License: GNU GPL 2.0: http://creativecommons.org/licenses/GPL/2.0/
     *    Example on: http://blog.aplusmedia.de/moo-gallery2
     *    Known issues:
     *    - preloading does not care about initialIndex param
     *    - hovering to a control over the border of the big image will make the other one flickering
     *    - if you enter and leave the control area very quickly, the control flickers sometimes
     *
     *    @param {HTMLElement} galleryElement The main gallery element
     *    @param {Array} thumbs, An array of HTML elements
     *    @param {HTMLElement} bigImageContainer, the full size image
     *    @param {HTMLElement} pageContainer, If you have several pages, put them in this container
     *    @param {Object} newOptions, You have to pass imagesPerPage if you have more than one!
     */
export class JSGallery2 {
    constructor(galleryElement, thumbs, bigImageContainer, pageContainer, newOptions) {
        const defaults = {
            'prevHandle': null,			//if you pass a previous page handle in here, it will be hidden if it's not needed
            'nextHandle': null,			//like above, but for next page
            'countHandle': null,		//handle of the counter variable
            'titleTarget': null,		//target HTML element where image texts are copied into
            'initialIndex': -1,			//which thumb to select after init. you could create deep links with it.
            'showSocialMediaButton': true,
            'showCartButton': true,
            'showCartConnector': false,
            'cartConnectorLinkRel': '',
            'activeClass': 'thumbnail-active', // the css class for the active thumbnail
            'touchContainerSelector' : '#bigimageContainer',
            'maxHeight': 280			//the maximum height in px one page of thumbnails may use
        };

        this.options = {...defaults, ...newOptions};

        this.galleryElement = galleryElement;
        this.nav = galleryElement.querySelector('.navigation');
        this.image = galleryElement.querySelector('.image');

        // one controller for every listener this instance registers, so destroy()
        // can detach all of them at once.
        this.abortController = new AbortController();

        // The index of the selected thumbnail is the single source of truth of
        // this gallery - the DOM (active class, big image, url, page offset) is
        // derived from it. -1 means "nothing selected yet".
        this.currentIndex = -1;
        this.currentPageNumber = -1;

        // How the thumbnails are spread over the pages is not configured but
        // measured, see distribute(). Until that ran, everything sits on one page.
        this.imagesPerFirstPage = 0;
        this.imagesPerPage = 0;
        this.lastPage = 1;

        this.thumbs = Array.from(thumbs);
        this.bigImageContainer = bigImageContainer;
        this.bigImage = bigImageContainer.querySelector('img');
        this.pageContainer = pageContainer;

        // the intro content of the first page, if the template renders it inline
        this.introElement = pageContainer.querySelector('.information-inline');

        // the <li> elements of the paging bar, created lazily by updatePagingBar()
        this.countLinks = [];

        this.convertThumbs();

        let url = document.location.href;
        this.initialIndex = parseInt(Helpers.getUrlHashParameterValue(url, 'imageno'));
        if (isNaN(this.initialIndex)) {
            this.initialIndex = 0;
        }

        this.createControls();

        this.createGallerySlides();
        this.adjustImageSize();
        this.distribute();

        this.gotoPage(0);

        if (this.options.initialIndex !== -1) {
            this.selectByIndex(this.options.initialIndex);
        } else if (this.initialIndex !== 0) {
            this.selectByIndex(this.initialIndex);
        }

        // everything is in place, the navigation may show itself
        this.nav.classList.add('is-ready');

        const signal = this.abortController.signal;
        window.addEventListener('load', () => {this.resizePage()}, {signal});
        window.addEventListener('resize', () => {this.resizePage()}, {signal});
        window.addEventListener('popstate', () => {this.selectFromUrl()}, {signal});

        this.observeLayoutChanges();
    }

    /**
     * The container of the currently selected thumbnail, derived from currentIndex.
     * @return {HTMLElement|undefined}
     */
    get selectedContainer() {
        return this.currentIndex === -1 ? undefined : this.thumbs[this.currentIndex];
    }

    /**
     * Measures the outer size of a rendered thumbnail and publishes it as a
     * custom property, so the stylesheet can build columns of exactly that
     * width. The template only knows the image size - the theme adds its own
     * padding and border on top - so this has to happen in the browser.
     *
     * @return {{width: Number, height: Number}} the outer size of one thumbnail
     */
    measureThumbSize() {
        const item = this.thumbs[0];
        const link = item.querySelector('a.event-thumbnail') ?? item;

        // Take the thumbnails out of the grid for the moment of the measurement.
        // Inside it their width is capped to the very column this measurement is
        // supposed to size (themes put max-width: 100% on the thumbnails), so
        // measuring there would only ever report the current column width back -
        // a stable, but wrong answer which no later measurement could correct.
        this.galleryElement.classList.add('is-measuring');

        // The width comes from the link, an inline-block sized by its content.
        // The height comes from the grid item, because that is what occupies a row.
        const size = {
            width: Math.round(link.getBoundingClientRect().width),
            height: Math.round(item.getBoundingClientRect().height)
        };

        this.galleryElement.classList.remove('is-measuring');

        if (size.width > 0) {
            this.galleryElement.style.setProperty('--eg-thumb-size', size.width + 'px');
        }

        return size;
    }

    /**
     * How much vertical space is used up before the thumbnails of the first page
     * start - the intro content plus whatever margins sit between it and the
     * grid.
     *
     * This measures the distance between the top of the page and the top of the
     * grid rather than the height of the intro element, because a margin of the
     * intro's last child collapses out of it and would otherwise be missed,
     * which made pages come out taller than the configured maximum.
     *
     * @param {HTMLElement} grid the .event-thumbnails of the first page
     * @return {Number}
     */
    measureIntroHeight(grid) {
        const page = grid.parentElement;
        if (page === null || !this.introElement) {
            return 0;
        }

        return Math.max(0, grid.getBoundingClientRect().top - page.getBoundingClientRect().top);
    }

    /**
     * Works out how many thumbnails fit on a page at the current window width.
     *
     * The number of columns is not calculated but read back from the resolved
     * grid, so the stylesheet stays the single place that decides how the
     * thumbnails are laid out. The number of rows follows from the configured
     * maximum height, where the first page has to share that height with the
     * intro content.
     *
     * @return {{perFirstPage: Number, perPage: Number}}
     */
    measureCapacity() {
        const grid = this.pageContainer.querySelector('.event-thumbnails');
        if (grid === null) {
            return {perFirstPage: this.thumbs.length, perPage: this.thumbs.length};
        }

        const thumbSize = this.measureThumbSize();
        const style = window.getComputedStyle(grid);
        const rowGap = parseFloat(style.rowGap) || 0;

        // "80px 80px 80px" -> 3 columns. An empty grid reports "none".
        const tracks = style.gridTemplateColumns;
        let columns = tracks && tracks !== 'none' ? tracks.split(' ').filter((t) => t !== '').length : 0;

        if (columns < 1) {
            columns = 1;
        }

        const rowHeight = thumbSize.height + rowGap;
        const rowsFor = (available) => rowHeight > 0 ? Math.max(1, Math.floor((available + rowGap) / rowHeight)) : 1;

        const maxHeight = this.options.maxHeight;
        const introHeight = this.measureIntroHeight(grid);

        return {
            perFirstPage: columns * rowsFor(maxHeight - introHeight),
            perPage: columns * rowsFor(maxHeight)
        };
    }

    /**
     * Spreads the thumbnails over as many pages as the current window width
     * needs and rebuilds the paging bar for the new number of pages.
     *
     * Does nothing when the capacity did not change, so a resize that does not
     * cross a column or row boundary is free.
     *
     * @param {Boolean} [force] rebuild even if the capacity is unchanged
     * @return {Boolean} whether the pages were rebuilt
     */
    distribute(force = false) {
        if (this.thumbs.length === 0) {
            return false;
        }

        const capacity = this.measureCapacity();

        if (!force
            && capacity.perFirstPage === this.imagesPerFirstPage
            && capacity.perPage === this.imagesPerPage) {
            return false;
        }

        this.imagesPerFirstPage = capacity.perFirstPage;
        this.imagesPerPage = capacity.perPage;
        this.lastPage = this.pageForIndex(this.thumbs.length - 1) + 1;

        this.buildPages();
        this.resetPagingBar();

        // the current page number refers to the old distribution, so force
        // showPage() to apply the page of the selected image again
        this.currentPageNumber = -1;

        return true;
    }

    /**
     * (Re)creates the page elements and moves the thumbnails into them.
     */
    buildPages() {
        const pages = [];

        for (let pageNumber = 0; pageNumber < this.lastPage; pageNumber++) {
            const page = document.createElement('div');
            page.classList.add('page');

            if (pageNumber === 0 && this.introElement) {
                page.appendChild(this.introElement);
            }

            const thumbnails = document.createElement('div');
            thumbnails.classList.add('event-thumbnails');

            const from = this.firstIndexOfPage(pageNumber);
            const to = Math.min(from + (pageNumber === 0 ? this.imagesPerFirstPage : this.imagesPerPage), this.thumbs.length);

            for (let i = from; i < to; i++) {
                thumbnails.appendChild(this.thumbs[i]);
            }

            page.appendChild(thumbnails);
            pages.push(page);
        }

        // the thumbnails are already detached from the old pages by appendChild
        this.pageContainer.replaceChildren(...pages);
    }

    /**
     * Throws the paging bar away so updateHandles() builds a fresh one for the
     * current number of pages.
     */
    resetPagingBar() {
        this.countLinks.forEach((element) => element.remove());
        this.countLinks = [];
    }

    /**
     * The page a thumbnail lives on.
     * @param {Number} index of the thumbnail, starting with 0
     * @return {Number} page number, starting with 0
     */
    pageForIndex(index) {
        if (index < this.imagesPerFirstPage) {
            return 0;
        }
        return Math.floor((index - this.imagesPerFirstPage) / this.imagesPerPage) + 1;
    }

    /**
     * The index of the first thumbnail of a page.
     * @param {Number} pageNumber starting with 0
     * @return {Number}
     */
    firstIndexOfPage(pageNumber) {
        if (pageNumber <= 0) {
            return 0;
        }
        return (pageNumber - 1) * this.imagesPerPage + this.imagesPerFirstPage;
    }

    /**
     * Detaches every listener this instance registered and removes the controls
     * it injected into the markup.
     */
    destroy() {
        this.abortController.abort();
        window.clearTimeout(this.eventgalleryAjaxResizeTimer);
        this.resizeObserver?.disconnect();

        [
            this.prevLink,
            this.zoomLink,
            this.nextLink,
            this.add2cartLink,
            this.cartConnectorLink,
            this.socialmediabutton,
            this.bigImageBackdrop
        ].forEach((element) => element?.remove());

        this.resetPagingBar();
        this.nav.classList.remove('is-ready');

        if (this.selectedContainer) {
            this.deselect(this.selectedContainer);
        }
        this.currentIndex = -1;
    }

    /* Method to bring the thumb rel attribute to the right size */
    adjustImageSize() {
        let sizeCalculator = new SizeCalculator();
        const stageWidth = Helpers.getElementWidth(this.bigImageContainer);
        const stageHeight = Helpers.getElementHeight(this.bigImageContainer);

        this.nav.querySelectorAll('#thumbs .event-thumbnail').forEach((thumbnail) =>  {

            let originalwidth = thumbnail.getAttribute('data-width');
            let originalheight = thumbnail.getAttribute('data-height');
            let     ratio = originalwidth / originalheight;
            let     secret = thumbnail.getAttribute('data-secret');

            // The stage shows the image with object-fit: contain, so it is
            // scaled down until it fits in *both* directions. Asking for the
            // full stage width would fetch a needlessly large file for
            // everything taller than the stage, which is most portrait images.
            let width = stageWidth;
            let height = Math.round(stageWidth / ratio);

            if (stageHeight > 0 && height > stageHeight) {
                height = Math.round(stageHeight);
                width = Math.round(stageHeight * ratio);
            }

            let imageUrl
            if (secret !== null) {
                let secret_o = thumbnail.getAttribute('data-secret_o');
                let secret_h = thumbnail.getAttribute('data-secret_h');
                let secret_k = thumbnail.getAttribute('data-secret_k');
                let farm = thumbnail.getAttribute('data-farm');
                let server = thumbnail.getAttribute('data-server');
                let id = thumbnail.getAttribute('data-file');

                 imageUrl = sizeCalculator.getFlickrURL(farm, server, secret, secret_h, secret_k, secret_o, id, width, height, originalwidth, originalheight);
            } else {
                let googleWidth = sizeCalculator.getSize(width, height, ratio);
                imageUrl = sizeCalculator.adjustImageURL(thumbnail.getAttribute('rel'), googleWidth);
            }
            thumbnail.setAttribute('rel', imageUrl);
        });
    }

    /*
     * Method which handles the case the window got resized. The layout of a
     * single page is done by CSS, but how many thumbnails fit on a page depends
     * on the window width, so they may have to be spread over a different
     * number of pages now.
     */
    resizePage() {
        window.clearTimeout(this.eventgalleryAjaxResizeTimer);

        this.eventgalleryAjaxResizeTimer = setTimeout(() => this.refreshLayout(), 500);
    }

    /**
     * Re-measures everything that depends on the size of the gallery and applies
     * the result. Cheap when nothing changed, because distribute() compares the
     * capacity before it touches the DOM.
     */
    refreshLayout() {
        this.adjustImageSize();
        this.distribute();
        // adjustImageSize() rewrote the image urls, so the big image has to
        // be loaded again - the selection itself does not change.
        this.resetThumbs();
        this.showPage(this.pageForIndex(this.currentIndex));
    }

    /**
     * Watches for changes which alter how many thumbnails fit on a page but do
     * not come with a window resize:
     *
     * - a web font arriving late changes the height of the intro content, and the
     *   first visitor of a site measures before that happened
     * - the gallery may sit in a container that changes width on its own, e.g. a
     *   tab or an accordion
     */
    observeLayoutChanges() {
        document.fonts?.ready?.then(() => this.refreshLayout()).catch(() => {});

        if (typeof ResizeObserver === 'undefined') {
            return;
        }

        this.lastObservedWidth = this.nav.getBoundingClientRect().width;
        this.resizeObserver = new ResizeObserver((entries) => {
            const width = entries[0].contentRect.width;

            // Only react to width changes. Redistributing changes the height of
            // the observed element, so reacting to height would loop.
            if (width === this.lastObservedWidth) {
                return;
            }

            this.lastObservedWidth = width;
            this.resizePage();
        });
        this.resizeObserver.observe(this.nav);
    }


    createGallerySlides() {
        this.slides = this.thumbs.map((thumbContainer) => {
            let lightboxLinkElement = thumbContainer.getElementsByTagName('A')[0];
            let title =  decodeURIComponent(lightboxLinkElement.getAttribute('data-description'));
            let slide =    {
                w: lightboxLinkElement.getAttribute('data-width'),
                h: lightboxLinkElement.getAttribute('data-height'),
                src: lightboxLinkElement.getAttribute('href'),
                title: title,
                type: lightboxLinkElement.getAttribute('data-type')
            };
            if (slide.type === 'video') {
                if (lightboxLinkElement.dataset.pswpVideoSources) {
                    slide.videoSources = JSON.parse(lightboxLinkElement.dataset.pswpVideoSources);
                } else if (lightboxLinkElement.dataset.pswpVideoSrc) {
                    slide.videoSrc = lightboxLinkElement.dataset.pswpVideoSrc;
                } else {
                    slide.videoSrc = lightboxLinkElement.getAttribute('href');
                }
            }
            return slide;
        });
    }

    createControls() {
        const signal = this.abortController.signal;

        // Fills whatever space object-fit: contain leaves next to the big image
        // with a blurred copy of it. Prepended so it stays behind everything else.
        this.bigImageBackdrop = document.createElement('img');
        this.bigImageBackdrop.id = 'bigImageBackdrop';
        this.bigImageBackdrop.alt = '';
        this.bigImageBackdrop.setAttribute('aria-hidden', 'true');
        this.bigImageContainer.prepend(this.bigImageBackdrop);

        // The controls are hidden and revealed by CSS (:hover / :focus-visible),
        // see .jsgallery-prev / -zoom / -next in ajax.less.
        this.prevLink = document.createElement('a');
        this.prevLink.href='#';
        this.prevLink.classList.add('link','jsgallery-prev');
        this.prevLink.addEventListener('click', (e)=> this.prevImage(e), {signal});

        this.zoomLink = document.createElement('a');
        this.zoomLink.href='#';
        this.zoomLink.classList.add('link','jsgallery-zoom');
        this.zoomLink.addEventListener('click', (e) => { e.preventDefault(); this.bigImageClickHandler(); }, {signal});

        this.nextLink = document.createElement('a');
        this.nextLink.href = '#';
        this.nextLink.classList.add('link','jsgallery-next');
        this.nextLink.addEventListener('click', (e) => this.nextImage(e), {signal});

		this.bigImage.parentElement.appendChild(this.prevLink);
        this.bigImage.parentElement.appendChild(this.zoomLink);
        this.bigImage.parentElement.appendChild(this.nextLink);

        this.bigImage.addEventListener('load', ()=>this.showBigImage(), {signal});

        if (this.options.showCartButton) {
            this.add2cartLink = document.createElement('a');
            this.add2cartLink.href = '#';
            this.add2cartLink.classList.add('eventgallery-add2cart','eventgallery-openAdd2cart','jsgallery-add2cart');
            this.add2cartLink.innerHTML = '<i class="egfa egfa-2x egfa-cart-plus"></i>';
            this.bigImage.parentElement.appendChild(this.add2cartLink);
            this.dispatchRebindAdd2CartButtons();
        }

        if (this.options.showCartConnector) {

            this.cartConnectorLink = document.createElement('a');
            this.cartConnectorLink.href = '#';
            this.cartConnectorLink.id = 'ajax-cartconnector';
            this.cartConnectorLink.classList.add('button-cart-connector','jsgallery-cartconnector');
            this.cartConnectorLink.innerHTML = '<i class="egfa egfa-2x egfa-cart-plus"></i>';
            this.cartConnectorLink.setAttribute('rel', this.options.cartConnectorLinkRel);
            this.bigImage.parentElement.appendChild(this.cartConnectorLink);
        }

        if (this.options.showSocialMediaButton) {
            let div = document.createElement('div');
            div.innerHTML = '<a id="ajax-social-media-button" class="social-share-button jsgallery-socialmedia" rel="nofollow" href="#"><i class="egfa egfa-2x egfa-share-alt-square"></i></a>';
            this.socialmediabutton = div.firstChild;
            this.socialmediabutton.addEventListener('click', e => {
                e.preventDefault();
                let href = this.socialmediabutton.getAttribute('data-href');
                let overlay = new Overlay();
                overlay.openOverlay(href);
            }, {signal});
            this.bigImage.parentElement.appendChild(this.socialmediabutton);
        }

        document.addEventListener('keydown', (e) => this.keyboardHandler(e), {signal});

		let swipeleft =  (e) => {
		    this.nextImage(e);
		};

		let swiperight = (e) => {
		    this.prevImage(e);
		};

        let tabaction = () => {
            this.bigImage.click();
        };

        addTouch(document.querySelector(this.options.touchContainerSelector),
            swiperight,
            swipeleft,
            tabaction,
            null,
        );
    }

    /**
     * Tells the mini cart that a new add2cart button showed up.
     */
    dispatchRebindAdd2CartButtons() {
        document.dispatchEvent(new CustomEvent(Events.rebindAdd2CartButtons, {detail: null}));
    }

    /**
     * Handles keyboard interactions.
     * @param {Event} event
     */
    keyboardHandler(event) {
        if (window.Eventgallery.lightbox?.isOpen() === true) {
            return;
        }

        const key = event.key;
        // Digits 1-9: jump to page 0-8
        if (/^[1-9]$/.test(key)) {
            event.preventDefault();
            this.gotoPage(parseInt(key, 10) - 1);
            return;
        }

        // Arrow navigation
        if (key === 'ArrowLeft') {
            event.preventDefault();
            this.prevImage(event);
            return;
        }
        if (key === 'ArrowRight') {
            event.preventDefault();
            this.nextImage(event);
        }
    }

    /**
     * Re-applies the current selection, e.g. after the image urls have been
     * recalculated for a new container width.
     */
    resetThumbs() {
        this.selectIndex(this.currentIndex, true);
    }

    /**
     *    Adds the border to the thumbs and so on. (conversion of static thumbs)
     */
    convertThumbs() {
        this.thumbs.forEach((thumbContainer, count) => {
            this.convertThumb(thumbContainer, count);
        })
    }

    /**
     * Converts one single thumb.
     * @param {HTMLElement} thumbContainer
     * @param {Number} count
     */
    convertThumb(thumbContainer, count) {

        if (thumbContainer === undefined) {
            return;
        }

        thumbContainer.addEventListener('click', (e) => {
            e.preventDefault();
            this.select(thumbContainer);
        }, {signal: this.abortController.signal});

        // no longer read by this class, but kept for themes and integrations
        // which style or script the thumbnails by their position.
        thumbContainer.setAttribute('data-counter', count);
    }

    /**
     *    Selects a certain image. (You have to pass the outer container of the image)
     *    @param {HTMLElement} container
     *    @param {Boolean} [forceReload]
     *    @return {Boolean} whether the selection was applied
     */
    select(container, forceReload = false) {
        return this.selectIndex(this.thumbs.indexOf(container), forceReload);
    }

    /**
     *    Selects the thumbnail with the given index and derives everything else
     *    from it: the active thumbnail, the visible page, the url, the overlay
     *    buttons and the big image.
     *
     *    @param {Number} index
     *    @param {Boolean} [forceReload]
     *    @return {Boolean} whether the selection was applied
     */
    selectIndex(index, forceReload = false) {
        if (index < 0 || index >= this.thumbs.length) {
            return false;
        }

        //this prevents an ugly effect if you click on the currently selected item
        if (index === this.currentIndex && !forceReload) {
            return false;
        }

        if (this.selectedContainer !== undefined) {
            this.deselect(this.selectedContainer);
        }

        this.currentIndex = index;
        const container = this.thumbs[index];

        this.updateUrl(index);

        //if the target image is not on the current page, we have to go there
        this.showPage(this.pageForIndex(index));

        container.classList.add(this.options.activeClass);

        //first link in the container
        let source = container.children[0];

        // prepare the add2cart button
        if (this.options.showCartButton) {
            if (container.getElementsByTagName('A')[0]?.getAttribute('data-type') === 'image') {
                this.add2cartLink.setAttribute('data-id', source.getAttribute('data-id'));
                this.add2cartLink.style.display =  'block';
            } else {
                this.add2cartLink.style.display =  'none';
            }
        }

        if (this.options.showCartConnector) {
            this.cartConnectorLink.setAttribute('data-folder', source.getAttribute('data-folder'));
            this.cartConnectorLink.setAttribute('data-file', source.getAttribute('data-file'));
            this.cartConnectorLink.setAttribute('href', decodeURIComponent(source.getAttribute('data-cart-connector-link')));
        }

        if (this.options.showSocialMediaButton) {
            this.socialmediabutton.setAttribute('data-href', decodeURIComponent(source.getAttribute('data-social-sharing-link')) );
        }

        this.dispatchRebindAdd2CartButtons();

        // now lets set the image
        this.setImage(source);

        return true;
    }


    /**
     * Selects an image by its thumbnail index. Indexes outside of the gallery
     * fall back to the first image.
     * @param {Number} index of the thumbnail, starting with 0
     */
    selectByIndex(index) {
        if (index < 0 || this.thumbs.length <= index) {
            index = 0;
        }
        this.selectIndex(index);
    }

    /**
     * Writes the selected image into the url so it can be linked to. This
     * replaces the current history entry on purpose - pushing one per image
     * would fill up the history and break the back button.
     * @param {Number} index
     */
    updateUrl(index) {
        if (!history || !history.replaceState) {
            return;
        }
        history.replaceState(history.state, '', Helpers.addUrlHashParameter(window.location.href, 'imageno', index));
    }

    /**
     * Applies the image referenced by the url, e.g. after the user navigated
     * back and forth or followed a deep link on the same page.
     */
    selectFromUrl() {
        const index = parseInt(Helpers.getUrlHashParameterValue(document.location.href, 'imageno'));
        if (!isNaN(index) && index !== this.currentIndex) {
            this.selectByIndex(index);
        }
    }

    /**
     *    Opposite to method above.
     *    @param {HTMLElement} container
     */
    deselect(container) {
        container.classList.remove(this.options.activeClass);
    }

    bigImageClickHandler() {
        this.createGallerySlides();
        window.Eventgallery.lightbox.openPhotoSwipe(
            this.currentIndex, this.slides, true, false, false
        );
        window.Eventgallery.lightbox.setAfterChangeEventListener(() => {
            let gid = window.Eventgallery.lightbox.getCurrentSlide().gid;
            if (this.slides.length>0 && gid === this.slides[0].gid) {
                this.selectByIndex(window.Eventgallery.lightbox.getCurrentIndex());
            }
        });
    }
    /**
     *    Changes the full size image to given one.
     *    @param lightboxLinkElement
     */
    setImage(lightboxLinkElement) {

            this.bigImage.style.opacity = '0.1';
            let newSrc = lightboxLinkElement.getAttribute('rel');
            let title =  decodeURIComponent(lightboxLinkElement.getAttribute('data-description'));

            if (window.Eventgallery.lightbox?.isOpen() === true) {
               window.Eventgallery.lightbox.gotoSlide(this.currentIndex);
            }

            const titleTarget = this.options.titleTarget ? this.galleryElement.querySelector(this.options.titleTarget) : null;
            if (titleTarget) {
                titleTarget.innerHTML = title ?? '';
            }

            this.bigImage.setAttribute('src', newSrc);
            this.bigImageBackdrop.setAttribute('src', newSrc);
    }

    showBigImage() {
        this.image.style.opacity = 1;
        this.bigImage.style.opacity = 1;
    }

    /**
     *    Selects the previous image.
     */
    prevImage(e) {
        if (e !== undefined) {
            e.preventDefault();
        }
        this.selectByIndex(this.currentIndex - 1);
    }
    /**
     *    Selects the next image.
     */
    nextImage(e) {
        if (e !== undefined) {
            e.preventDefault();
        }
        this.selectByIndex(this.currentIndex + 1);
    }

    /**
     * Slides the given page into view and updates the paging handles. This does
     * not touch the selection - selectIndex() calls it to follow the selected
     * thumbnail.
     *
     * Every page is exactly as wide as the track (see ajax.less), so shifting
     * the track by whole percentages moves it by whole pages - independent of
     * the actual pixel width.
     *
     * @param {Number} pageNumber
     * @return {Boolean} whether the page is now visible
     */
    showPage(pageNumber) {
        if (pageNumber < 0 || pageNumber >= this.lastPage) {
            return false;
        }

        if (pageNumber === this.currentPageNumber) {
            return true;
        }

        this.currentPageNumber = pageNumber;
        // one page plus the gutter between two pages, see --eg-page-gap in ajax.less
        this.pageContainer.style.transform =
            'translateX(calc((-100% - var(--eg-page-gap, 0px)) * ' + pageNumber + '))';
        this.updateHandles();

        return true;
    }

    /**
     * Navigates to given page and selects the first image of it.
     * Also hides the handles (if set).
     * @param pageNumber {Number}
     */
    gotoPage(pageNumber) {
        if (!this.showPage(pageNumber)) {
            return;
        }

        this.selectIndex(this.firstIndexOfPage(pageNumber));
    }

    updateHandles() {
        //update handles

        if (this.options.prevHandle) {
            this.options.prevHandle.style.opacity = this.currentPageNumber === 0 ? 0 : 1;
        }
        if (this.options.nextHandle) {
            this.options.nextHandle.style.opacity = this.currentPageNumber === this.lastPage - 1 ? 0 : 1;
        }

        if (this.options.countHandle) {
            this.updatePagingBar(this.currentPageNumber, this.lastPage);
        }

    }

    updatePagingBar(currentPage, pageCount) {
        if (pageCount <= 1 ) {
            return;
        }

        //init the pagingbar
        if (this.countLinks.length === 0) {
            for (let i = 0; i < pageCount; i++) {
                this.createCountLink(i);
            }
        }

        if (pageCount > 9) {

            this.countLinks.forEach((item) => { item.hidden = false; });

            let skipFromRight = pageCount;
            let skipFromLeft = 0;

            let spaceToRight = pageCount - currentPage - 1;
            let spaceToLeft = currentPage;

            if (spaceToLeft > 4 && spaceToRight > 4) {
                skipFromLeft = currentPage - 4;
                skipFromRight = currentPage + 5;
            } else {
                if (spaceToLeft <= 4) {
                    skipFromLeft = 0;
                    skipFromRight = currentPage + 5 + (4 - spaceToLeft);
                }
                if (spaceToRight <= 4) {
                    skipFromLeft = currentPage - 4 - (4 - spaceToRight);
                    skipFromRight = pageCount;
                }
            }

            for (let i = 0; i < skipFromLeft; i++) {
                this.countLinks[i].hidden = true;
            }

            for (let i = skipFromRight; i < pageCount; i++) {
                this.countLinks[i].hidden = true;
            }
        }

        this.countLinks.forEach((item) => item.classList.remove('active'));
        this.countLinks[currentPage].classList.add('active');

    }

    createCountLink(currentPageNumber) {

        let myAnchor = document.createElement('A');
        myAnchor.href = '#';
        myAnchor.classList.add('page-link');
        myAnchor.textContent = currentPageNumber + 1;

        myAnchor.addEventListener('click',(e) => {
            e.preventDefault();
            this.gotoPage(currentPageNumber);
        }, {signal: this.abortController.signal});

        let myListItem = document.createElement('LI');
        myListItem.classList.add('count');
        myListItem.classList.add('page-item');
        myListItem.id = 'count' + currentPageNumber;

        myListItem.appendChild(myAnchor);

        this.countLinks.push(myListItem);
        this.options.countHandle.appendChild(myListItem);
    }
}
